/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getApplications} from '@ion/core/src/services/Connect';
import {Applications} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type useAccountApplicationsResult = UseDefaultQueryResult & {
  applications: Applications;
};

export interface useAccountApplicationsParams {
  accountId: number
};

/**
 * Retrieves the list of applications for the given account id.
 *
 * @param accountId of the applications to retrieve the applications list.
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns a query result
 */
export const useAccountApplications = (accountId, enabled = true, staleTime = getStaleTime(5, false, false)
): useAccountApplicationsResult => {
  const query     = useQuery([QueryKey.ACCOUNT_APPLICATIONS, accountId], async() => {
    try {
      const results = await getApplications(accountId);
      return results;
    } catch (err) {
      error('useAccountApplications', err);
      return [];
    }
  }, {staleTime: staleTime as number, enabled});

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  return createQueryResult(query, {
    applications : query.data || {}
  }) as useAccountApplicationsResult;
};
export default useAccountApplications;
